import { useState, useEffect, useCallback } from "react";
import { BREAKPOINTS, resolveBreakpoint, BreakpointContext, BP_ORDER, type Breakpoint, type UseBreakpointReturn } from "./useBreakpoint";

interface BreakpointProviderProps {
  children: React.ReactNode;
}

/**
 * Mount once near the root of the app. All useBreakpoint() calls inside will
 * read from this context instead of each registering their own matchMedia listeners.
 */
export const BreakpointProvider = ({ children }: BreakpointProviderProps) => {
  const [breakpoint, setBreakpoint] = useState<Breakpoint>(resolveBreakpoint);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const queries = BREAKPOINTS.filter(({ minWidth }) => minWidth > 0).map(({ name, minWidth }) => ({
      name,
      mql: window.matchMedia(`(min-width: ${minWidth}px)`),
    }));

    const handleChange = () => {
      const next = resolveBreakpoint();
      setBreakpoint((prev) => (prev === next ? prev : next));
    };

    queries.forEach(({ mql }) => mql.addEventListener("change", handleChange));

    return () => {
      queries.forEach(({ mql }) => mql.removeEventListener("change", handleChange));
    };
  }, []);

  const isMin = useCallback((bp: Breakpoint) => BP_ORDER.indexOf(breakpoint) >= BP_ORDER.indexOf(bp), [breakpoint]);
  const isBelow = useCallback((bp: Breakpoint) => BP_ORDER.indexOf(breakpoint) < BP_ORDER.indexOf(bp), [breakpoint]);

  const value: UseBreakpointReturn = {
    breakpoint,
    isMin,
    isBelow,
    isXs: true,
    isSm: isMin("sm"),
    isMd: isMin("md"),
    isLg: isMin("lg"),
    isXl: isMin("xl"),
    is2Xl: isMin("2xl"),
  };

  return <BreakpointContext.Provider value={value}>{children}</BreakpointContext.Provider>;
};
